import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { LayoutChangeEvent, ScrollView as ScrollViewRN } from "react-native";
import { ALIGN, COLOR, Box, Layout, Panel, View, useDevice } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { ItemToReturn, Order, ProductWithFeedback } from "../../../../../../domain/entities/order/Order";
import { ItemStatus } from "../../../../../../domain/entities/product/Product";
import { Question } from "../../../../../../domain/entities/question/Question";
import { useRequestReturnOrderItems } from "../../../../behaviors/useRequestReturnOrderItems";
import { useToast } from "../../../../hooks/useToast";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { PanelHeader } from "../../header/panelHeader/PanelHeader";
import { FeedbackList } from "../feedbackList/FeedbackList";
import { style as feedbackPanelStyle } from "./FeedbackPanel.style";

interface FeedbackPanelProps {
  readonly currentOrder: Order; // TODO: this should be a projection
  readonly questions: Record<string, Question[]>; // TODO: Question should be a projection
  readonly selectedItems: ProductWithFeedback[];
  readonly onBack: () => void;
  readonly onNext: () => void;
}

const FeedbackPanel: FC<FeedbackPanelProps> = ({ currentOrder, questions, selectedItems, onBack, onNext }) => {
  const { screen } = useDevice();
  const toast = useToast();
  const requestReturnItems = useRequestReturnOrderItems();
  const [itemsPositions, setItemsPositions] = useState<Record<string, number>>({});
  const scrollViewRef = useRef<ScrollViewRN>(null);
  const isDesktopScreen = screen.L;

  const confirmReturnFeedbackErrorText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CONFIRM_RETURN_FEEDBACK_ERROR,
  });

  const style = useMemo(() => feedbackPanelStyle(), []);

  const questionsForSelectedItems = useMemo(() => {
    if (!questions || !selectedItems) {
      return {};
    }
    const selectedItemsIds = selectedItems.map((item) => item._variantId);
    return selectedItemsIds.reduce(
      (acc, itemId) => {
        const itemQuestions = questions[itemId];
        if (itemQuestions) {
          acc[itemId] = itemQuestions;
        }
        return acc;
      },
      {} as Record<string, Question[]>,
    );
  }, [selectedItems, questions]);

  const onPressReturnButton = useCallback(async () => {
    const { orderId, serviceId } = currentOrder;

    const itemsToReturn = selectedItems.map(({ itemId, feedback }) => {
      return {
        itemId,
        newStatus: ItemStatus.TO_RETURN,
        feedback,
      } as ItemToReturn;
    });
    requestReturnItems({
      orderId,
      serviceId,
      items: itemsToReturn,
      onSuccess: () => {
        onNext?.();
      },
      onError: () => {
        toast.showError({ message: confirmReturnFeedbackErrorText });
      },
    });
  }, [currentOrder, selectedItems, requestReturnItems, onNext, toast, confirmReturnFeedbackErrorText]);

  const onQuestionsUnavailable = useCallback(
    () => toast.showError({ message: confirmReturnFeedbackErrorText }),
    [toast, confirmReturnFeedbackErrorText],
  );

  const onLayout = useCallback((event: LayoutChangeEvent, productId: string) => {
    const { y } = event.nativeEvent.layout;
    setItemsPositions((prev) => ({
      ...prev,
      [productId]: y,
    }));
  }, []);

  const onError = useCallback(
    (productId: string) => {
      if (scrollViewRef.current) {
        const positionY = itemsPositions[productId];
        scrollViewRef.current.scrollTo({ y: positionY, animated: true });
      }
    },
    [itemsPositions],
  );

  return (
    <Panel
      align={ALIGN.RIGHT}
      alignItems={ALIGN.CENTER}
      color={screen.L ? COLOR.BG_PRIMARY_LIGHT : COLOR.BG_BASE}
      testID="feedback-panel"
      isVisible
      onClose={() => void 0}
    >
      <PanelHeader onBack={onBack} />
      <ScrollViewRN ref={scrollViewRef}>
        <Layout fullWidth={!isDesktopScreen} style={isDesktopScreen ? style.layout : undefined}>
          <Box align={ALIGN.CENTER} size={{ L: "2/3" }} style={isDesktopScreen ? style.detailWrapperLarge : undefined}>
            <View style={isDesktopScreen ? style.detailLarge : style.detailSmall}>
              <FeedbackList
                questions={questionsForSelectedItems}
                selectedItems={selectedItems}
                onBack={onBack}
                onError={onError}
                onLayout={onLayout}
                onNext={onPressReturnButton}
                onQuestionsUnavailable={onQuestionsUnavailable}
              />
            </View>
          </Box>
        </Layout>
      </ScrollViewRN>
    </Panel>
  );
};

export type { FeedbackPanelProps };
export { FeedbackPanel };
